import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject } from '@angular/core';
import {Router} from '@angular/router';
import {ECheckoutStatus} from '@app/checkout/enums/checkout-status.enum';
import {CheckoutStep} from '@app/checkout/enums/checkout-step.enum';
import {OrderStatusEnum} from '@app/checkout/enums/order-status.enum';
import {ICheckoutComponent} from '@app/checkout/models/checkout-component.model';
import {ICustomerOrganization, IOrganizationState} from '@app/shared/models/header.model';
import {StepperService} from '@app/shared/services/stepper.service';
import {selectCustomerOrganizationAndAddress} from '@app/shared/state/header/header.selector';
import {Store} from '@ngrx/store';
import {Subject, takeUntil, tap} from 'rxjs';
import {Order} from '../../models/order-request.model';
import {resetCheckoutStatus, updateOrderFromBillingInfo} from '../../state/checkout.actions';
import {selectCheckoutStatus, selectOrderForm} from '../../state/checkout.selector';
import { Bind } from 'primeng/bind';
import { Checkbox } from 'primeng/checkbox';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RadioButton } from 'primeng/radiobutton';
import { InputText } from 'primeng/inputtext';
import { DatePicker } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-billing-info',
    templateUrl: './billing-info.component.html',
    styleUrls: ['./billing-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        Bind,
        Checkbox,
        FormsModule,
        RadioButton,
        ReactiveFormsModule,
        InputText,
        DatePicker,
        Select,
        TranslatePipe,
    ],
})
export class BillingInfoComponent implements OnDestroy, ICheckoutComponent {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly stepperService = inject(StepperService);
  private readonly cdr = inject(ChangeDetectorRef);

  customerOrganization: IOrganizationState | null = null;
  orderData: Order | undefined;
  private readonly unsubscribe$ = new Subject<void>();
  checkoutState: ECheckoutStatus | undefined;
  checkoutStatus = ECheckoutStatus;
  address = '';
  nextInProgress = false;
  previousInProgress = false;
  cancelInProgress = false;
  draftInProgress = false;
  isGermanChecked: boolean = false;
  isGermanNotChecked: boolean = false;
  hideCountrySelection = true;

  constructor() {
    this.store
      .select(selectOrderForm)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((order: Order) => {
          this.orderData = order;
        })
      )
      .subscribe();

    this.store
      .select(selectCustomerOrganizationAndAddress)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(
          (customerOrganizationState: {
            customerOrganization: ICustomerOrganization | null;
            customerOrganizationAddress: string;
          }) => {
            this.customerOrganization = customerOrganizationState.customerOrganization?.organization ?? null;
            this.address = customerOrganizationState.customerOrganizationAddress;
          }
        )
      )
      .subscribe();

    this.store
      .select(selectCheckoutStatus)
      .pipe(
        tap((checkoutStatus: ECheckoutStatus) => {
          this.checkoutState = checkoutStatus;
          if (checkoutStatus === ECheckoutStatus.SuccessFromBillingInfo) {
            this.stepperService.emitNext(CheckoutStep.PatientInfo);
          }
          if (
            checkoutStatus === ECheckoutStatus.SuccessFromBillingInfo ||
            checkoutStatus === ECheckoutStatus.ErrorFromBillingInfo
          ) {
            this.nextInProgress = false;
            this.draftInProgress = false;
            this.cdr.markForCheck();
          }
        })
      )
      .subscribe();
  }

  disableNext() {
    return false;
  }

  next() {
    if (
      !this.orderData?.institutionName ||
      !this.orderData?.address ||
      !this.orderData?.country ||
      !this.orderData?.region ||
      !this.orderData?.city
    ) {
      const lastCompletedStep = this.orderData?.lastCompletedStep ?? -1;
      this.nextInProgress = true;
      if (this.orderData) {
        this.orderData = {
          ...this.orderData,
          lastCompletedStep:
            lastCompletedStep > CheckoutStep.BillingInfo ? lastCompletedStep : CheckoutStep.BillingInfo,
          currentStep: CheckoutStep.PatientInfo,
          orderStatusId: OrderStatusEnum.New.toString(),
        };
      }
      this.store.dispatch(resetCheckoutStatus());
      this.saveOrder(false);
    } else {
      this.stepperService.emitNext(CheckoutStep.PatientInfo);
    }
  }

  previous() {
    this.stepperService.emitPrevious(CheckoutStep.OrderInfo);
  }

  cancelOrder() {
    // use for custom cancel logic
  }

  saveDraftOrder() {
    this.draftInProgress = true;
    if (this.orderData) {
      this.orderData = {
        ...this.orderData,
        orderStatusId: OrderStatusEnum.New.toString(),
      };
    }
    this.saveOrder(true);
  }

  nextLoading() {
    return this.nextInProgress;
  }

  previousLoading() {
    return this.previousInProgress;
  }

  cancelLoading() {
    return this.cancelInProgress;
  }

  draftOrSubmitLoading() {
    return this.draftInProgress;
  }

  saveOrder(isDraft = false) {
    let orderData = {
      ...this.orderData,
      productId: this.orderData?.orderItems[0]?.productId ?? this.orderData?.productId,
      currentStep: isDraft ? this.orderData?.currentStep : CheckoutStep.BillingInfo,
    } as Order;

    if (!isDraft) {
      orderData = {
        ...orderData,
        address: this.orderData?.address ? this.orderData?.address : this.customerOrganization?.address,
        country: this.orderData?.country ?? this.customerOrganization?.countryName,
        region: this.orderData?.region ?? this.customerOrganization?.regionName,
        city: this.orderData?.city ?? this.customerOrganization?.cityName,
        zipPostalCode: this.orderData?.zipPostalCode
          ? this.orderData?.zipPostalCode
          : this.customerOrganization?.zipPostalCode,
        institutionName: this.orderData?.institutionName
          ? this.orderData?.institutionName
          : this.customerOrganization?.name,
      } as Order;
    }
    this.store.dispatch(updateOrderFromBillingInfo({orderRequest: orderData, isDraft: isDraft}));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
